class SkeletonBase extends HTMLElement {
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }

    /**
     * Génère une dimension en ajoutant l'unité si nécessaire
     *
     * @param {string} size
     * @param {boolean} fallbackTo100
     */
    size(size, fallbackTo100) {
        if (size) {
            if (size.match(/^[0-9]+$/)) {
                size += 'px';
            }
            return size;
        } else if (fallbackTo100) {
            return '100%';
        }
        return 'auto';
    }
}

class SkeletonThumbnail extends SkeletonBase {
    connectedCallback() {
        let size = this.getAttribute('size');
        if (!size) size = 'small';
        this.root.innerHTML = `<style>
      @keyframes skeletonShimmerAnimation {
        0% { opacity: 0.45; }
        100% { opacity: 0.9; }
      }
      .skeleton-thumbnail {
        animation: skeletonShimmerAnimation 850ms linear infinite alternate;
        will-change: opacity;
        position: relative;
      }
      .skeleton-thumbnail:after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: block;
        background: var(--color-primary-03);
        border-radius: .3rem;
      }
      @media screen and (-ms-high-contrast: active) {
        .skeleton-thumbnail:after {
          background: grayText;
        }
      }
      .skeleton-thumbnail--size-small {
        height: 4rem;
        width: 4rem;
      }
      .skeleton-thumbnail--size-medium {
        height: 6rem;
        width: 6rem;
      }
      .skeleton-thumbnail--size-large {
        height: 8rem;
        width: 8rem;
      }
      .skeleton-thumbnail--size-adapt {
        padding-bottom: 100%;
        height: 100%;
        width: 100%;
      }
    </style>
    <div class="skeleton-thumbnail skeleton-thumbnail--size-${size}"></div>`;
    }
}

class SkeletonDisplayText extends SkeletonBase {
    connectedCallback() {
        const size = this.getAttribute('size');
        const width = this.size(this.getAttribute('width'));
        this.root.innerHTML = `<style>
      @keyframes skeletonShimmerAnimation {
        0% { opacity: 0.45; }
        100% { opacity: 0.9; }
      }
      .skeleton-display-text__display-text {
        animation: skeletonShimmerAnimation 850ms linear infinite alternate;
        will-change: opacity;
        width: ${width};
        position: relative;
      }
      .skeleton-display-text__display-text:after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: block;
        background: var(--color-primary-03);
        border-radius: .3rem;
      }
      @media screen and (-ms-high-contrast: active) {
        .skeleton-display-text__display-text:after {
          background: grayText;
        }
      }
      .skeleton-display-text--size-extra-small { height: 1.4rem; }
      .skeleton-display-text--size-small { height: 2.4rem; }
      @media (min-width: 40em) { .skeleton-display-text--size-small { height: 2.8rem; } }
      .skeleton-display-text--size-medium { height: 2.8rem; }
      @media (min-width: 40em) { .skeleton-display-text--size-medium { height: 3.2rem; } }
      .skeleton-display-text--size-large { height: 2.8rem; }
      @media (min-width: 40em) { .skeleton-display-text--size-large { height: 3.2rem; } }
      .skeleton-display-text--size-extra-large { height: 3.6rem; }
      @media (min-width: 40em) { .skeleton-display-text--size-extra-large { height: 4.4rem; } }
    </style>
    <div class="skeleton-display-text__display-text skeleton-display-text--size-${size}"></div>`;
    }
}

class SkeletonBodyText extends SkeletonBase {
    connectedCallback() {
        let lines = this.getAttribute('lines');
        const width = this.size(this.getAttribute('width'));
        if (!lines) lines = 1;
        let bodyTextLines = '<div class="skeleton-body-text"></div>';
        if (lines) {
            for (let i = 1; i < parseInt(lines, 10); i++) {
                bodyTextLines += '<div class="skeleton-body-text"></div>';
            }
        }
        this.root.innerHTML = `<style>
      @keyframes skeletonShimmerAnimation {
        0% { opacity: 0.45; }
        100% { opacity: 0.9; }
      }
      .skeleton-body-text__skeleton-body-text-container {
        animation: skeletonShimmerAnimation 850ms linear infinite alternate;
        will-change: opacity;
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin-top: 1rem;
        margin-bottom: 3rem;
      }
      .skeleton-body-text {
        width: ${width};
        min-width: 2rem;
        height: 0.6rem;
        position: relative;
      }
      .skeleton-body-text::after {
        content: '';
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: block;
        height: .8rem;
        background: var(--color-primary-03);
        border-radius: 6px;
      }
      .skeleton-body-text:last-child:not(:first-child) {
        width: 80%;
      }
      .skeleton-body-text + .skeleton-body-text {
        margin-top: 1rem;
      }
    </style>
    <div class="skeleton-body-text__skeleton-body-text-container">${bodyTextLines}</div>`;
    }
}

if (!customElements.get('skeleton-thumbnail')) {
  customElements.define('skeleton-thumbnail', SkeletonThumbnail);
}
if (!customElements.get('skeleton-display-text')) {
  customElements.define('skeleton-display-text', SkeletonDisplayText);
}
if (!customElements.get('skeleton-body-text')) {
  customElements.define('skeleton-body-text', SkeletonBodyText);
}
